(function () {
  const SLIDE_FADE_MS = 700;
  const VIDEO_END_THRESHOLD_S = 0.05;
  const HTML_VIDEO_SELECTOR = "video.media-content-slider__feature-media-video";
  const IFRAME_VIDEO_SELECTOR =
    "iframe.media-content-slider__feature-media-video--iframe";

  const sectionInstances = new WeakMap();

  class MediaContentSlider {
    constructor(el) {
      this.el = el;
      this.navItems = Array.from(
        el.querySelectorAll(".media-content-slider__nav-item")
      );
      this.bullets = Array.from(
        el.querySelectorAll(".media-content-slider__bullet")
      );
      this.visuals = Array.from(
        el.querySelectorAll(".media-content-slider__visual")
      );
      this.total = this.visuals.length;
      this.initialized = false;

      if (this.total <= 1) return;

      this.current = 0;
      this.autoplay = el.dataset.autoplay === "true";
      this.autoplayDelay = parseInt(el.dataset.autoplayDelay, 10) || 6000;
      this.matchVideoDuration = el.dataset.matchVideoDuration === "true";
      this.raf = null;
      this.startTime = null;
      this.timerElapsedMs = 0;
      this.timerDelayMs = 0;
      this.progressRunId = 0;
      this.isInViewport = false;
      this.intersectionObserver = null;
      this.videoProgressCleanup = null;
      this.leaveTimer = null;
      this.eventController = new AbortController();

      // The nav item is the tab, so it owns both pointer and keyboard activation.
      this.navItems.forEach((item, i) => {
        item.addEventListener("click", () => this.goTo(i), {
          signal: this.eventController.signal,
        });

        item.addEventListener(
          "keydown",
          (event) => this.handleTabKeydown(event, i),
          { signal: this.eventController.signal }
        );
      });

      this.updateBulletStates(this.current);
      this.scrollNavItemIntoView(this.current, { instant: true });
      this.setupIntersectionObserver();

      this.visuals.forEach((visual, i) => {
        if (i !== this.current) this.pauseSlideMedia(visual);
      });

      this.initialized = true;
    }

    destroy() {
      if (!this.initialized) return;

      this.stopProgress();
      this.eventController.abort();
      this.clearLeaveTimer();
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
    }

    clearLeaveTimer() {
      if (!this.leaveTimer) return;
      clearTimeout(this.leaveTimer);
      this.leaveTimer = null;
    }

    handleTabKeydown(event, index) {
      let nextIndex = null;

      // A div with role="tab" gets no native activation, so Enter/Space are handled here.
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.goTo(index);
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        nextIndex = (index + 1) % this.total;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        nextIndex = (index - 1 + this.total) % this.total;
      }

      if (event.key === "Home") nextIndex = 0;
      if (event.key === "End") nextIndex = this.total - 1;

      if (nextIndex === null) return;

      event.preventDefault();
      this.goTo(nextIndex);
      this.navItems[nextIndex]?.focus();
    }

    getActiveHtmlVideo() {
      return this.visuals[this.current]?.querySelector(HTML_VIDEO_SELECTOR);
    }

    pauseSlideMedia(visual, { reset = true } = {}) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.pause();
        if (reset) video.currentTime = 0;
      });

      visual
        .querySelectorAll(IFRAME_VIDEO_SELECTOR)
        .forEach((iframe) => this.pauseIframe(iframe));
    }

    resumeSlideMedia(visual) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.play().catch(() => {});
      });

      visual
        .querySelectorAll(IFRAME_VIDEO_SELECTOR)
        .forEach((iframe) => this.playIframe(iframe));
    }

    restartSlideMedia(visual) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.loop = !this.matchVideoDuration;
        video.currentTime = 0;
        video.play().catch(() => {});
      });

      visual
        .querySelectorAll(IFRAME_VIDEO_SELECTOR)
        .forEach((iframe) => this.restartIframe(iframe));
    }

    pauseIframe(iframe) {
      const src = iframe.src;

      if (src.includes("youtube.com")) {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*"
        );
        return;
      }

      if (src.includes("player.vimeo.com")) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
      }
    }

    playIframe(iframe) {
      const src = iframe.src;

      if (src.includes("youtube.com")) {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*"
        );
        return;
      }

      if (src.includes("player.vimeo.com")) {
        iframe.contentWindow?.postMessage('{"method":"play"}', "*");
      }
    }

    restartIframe(iframe) {
      const src = iframe.getAttribute("src");
      if (!src) return;
      iframe.setAttribute("src", src);
    }

    goTo(index) {
      if (index === this.current) return;

      this.stopProgress();
      this.clearLeaveTimer();

      const prev = this.current;
      this.current = index;
      const prevVisual = this.visuals[prev];
      const nextVisual = this.visuals[index];

      this.visuals.forEach((visual) => visual.classList.remove("is-leaving"));

      // Only the media panel transitions; the nav just swaps its active state.
      prevVisual.classList.remove("is-active");
      prevVisual.classList.add("is-leaving");
      nextVisual.classList.remove("is-leaving");
      nextVisual.classList.add("is-active");
      prevVisual.setAttribute("aria-hidden", "true");
      nextVisual.setAttribute("aria-hidden", "false");

      this.leaveTimer = setTimeout(() => {
        prevVisual.classList.remove("is-leaving");
        this.leaveTimer = null;
      }, SLIDE_FADE_MS);

      this.pauseSlideMedia(prevVisual, { reset: true });
      this.restartSlideMedia(nextVisual);

      this.navItems[prev].classList.remove("is-active");
      this.navItems[index].classList.add("is-active");

      this.updateBulletStates(index);
      this.scrollNavItemIntoView(index);

      if (this.autoplay && this.isInViewport) this.startProgress();
    }

    next() {
      this.goTo((this.current + 1) % this.total);
    }

    updateBulletStates(activeIndex) {
      // tabindex stays 0 on every tab so Tab walks the whole list; only the state changes.
      this.navItems.forEach((item, i) => {
        item.setAttribute("aria-selected", i === activeIndex ? "true" : "false");
      });

      this.bullets.forEach((bullet, i) => {
        const isActive = i === activeIndex;

        bullet.classList.toggle("is-active", isActive);
        if (!isActive) this.writeFill(bullet, 0);
      });

      const activeBullet = this.bullets[activeIndex];
      if (activeBullet) this.setFill(activeBullet, 0);
    }

    scrollNavItemIntoView(index, { instant = false } = {}) {
      const nav = this.el.querySelector(".media-content-slider__nav");
      const target = this.navItems[index];
      if (!nav || !target) return;
      if (nav.scrollWidth <= nav.clientWidth + 1) return;

      target.scrollIntoView({
        behavior: instant ? "auto" : "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }

    advanceFromVideoEnd(runId) {
      if (!this.matchVideoDuration) return;
      if (runId !== this.progressRunId) return;

      const video = this.getActiveHtmlVideo();
      if (video) {
        video.loop = false;
        if (Number.isFinite(video.duration) && video.duration > 0) {
          video.currentTime = Math.max(video.duration - 0.001, 0);
        }
        video.pause();
      }

      this.stopProgress(false);
      this.next();
    }

    startProgress({ resume = false } = {}) {
      if (!this.autoplay || !this.isInViewport) return;

      this.stopProgress();
      if (!resume) this.updateBulletStates(this.current);

      const video = this.getActiveHtmlVideo();
      if (this.matchVideoDuration && video) {
        if (!resume) {
          this.timerElapsedMs = 0;
          this.timerDelayMs = 0;
        }
        this.startVideoSyncedProgress(video);
        return;
      }

      this.startTimerProgress({ resume });
    }

    startVideoSyncedProgress(video) {
      if (!this.matchVideoDuration) {
        this.startTimerProgress();
        return;
      }

      const runId = ++this.progressRunId;
      const bullet = this.bullets[this.current];
      if (!bullet) return;

      const beginSync = () => {
        if (runId !== this.progressRunId) return;
        if (!Number.isFinite(video.duration) || video.duration <= 0) {
          this.startTimerProgress();
          return;
        }

        video.loop = false;

        const onTimeUpdate = () => {
          if (runId !== this.progressRunId) return;

          this.setFill(bullet, Math.min(video.currentTime / video.duration, 1));

          if (video.duration - video.currentTime <= VIDEO_END_THRESHOLD_S) {
            this.advanceFromVideoEnd(runId);
          }
        };

        const onEnded = () => this.advanceFromVideoEnd(runId);

        video.addEventListener("timeupdate", onTimeUpdate);
        video.addEventListener("ended", onEnded);

        this.videoProgressCleanup = () => {
          video.removeEventListener("timeupdate", onTimeUpdate);
          video.removeEventListener("ended", onEnded);
        };

        if (video.paused) video.play().catch(() => {});

        onTimeUpdate();
      };

      if (
        video.readyState >= 1 &&
        Number.isFinite(video.duration) &&
        video.duration > 0
      ) {
        beginSync();
        return;
      }

      const onReady = () => beginSync();
      video.addEventListener("loadedmetadata", onReady, { once: true });
      this.videoProgressCleanup = () => {
        video.removeEventListener("loadedmetadata", onReady);
      };
    }

    startTimerProgress({ resume = false } = {}) {
      const currentDelay = this.matchVideoDuration
        ? this.getCurrentSlideDelay()
        : this.autoplayDelay;
      const runId = ++this.progressRunId;
      const bullet = this.bullets[this.current];
      if (!bullet) return;

      if (!resume) {
        this.timerElapsedMs = 0;
        this.timerDelayMs = currentDelay;
      } else if (!this.timerDelayMs) {
        this.timerDelayMs = currentDelay;
      }

      const effectiveDelay = this.timerDelayMs || currentDelay;

      this.startTime = null;
      const tick = (timestamp) => {
        if (runId !== this.progressRunId) return;

        if (this.startTime === null) {
          this.startTime = timestamp - this.timerElapsedMs;
        }

        this.timerElapsedMs = Math.min(
          Math.max(timestamp - this.startTime, 0),
          effectiveDelay
        );

        const progress = Math.min(this.timerElapsedMs / effectiveDelay, 1);
        this.setFill(bullet, progress);

        if (progress < 1) {
          this.raf = requestAnimationFrame(tick);
          return;
        }

        this.timerElapsedMs = 0;
        this.timerDelayMs = 0;
        this.next();
      };

      this.raf = requestAnimationFrame(tick);
    }

    stopProgress(clearVideoListeners = true, preserveTimerProgress = false) {
      this.progressRunId += 1;

      if (
        preserveTimerProgress &&
        this.startTime !== null &&
        this.timerDelayMs
      ) {
        this.timerElapsedMs = Math.min(
          Math.max(performance.now() - this.startTime, 0),
          this.timerDelayMs
        );
      }

      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
      this.startTime = null;

      if (!preserveTimerProgress) {
        this.timerElapsedMs = 0;
        this.timerDelayMs = 0;
      }

      if (clearVideoListeners && this.videoProgressCleanup) {
        this.videoProgressCleanup();
        this.videoProgressCleanup = null;
      }
    }

    // CSS owns the axis — vertical rail on desktop, horizontal under the text on mobile — so
    // the script only ever reports progress.
    writeFill(bullet, progress) {
      const fill = bullet?.querySelector(".media-content-slider__bullet-fill");
      if (!fill) return;

      fill.style.setProperty("--fill-progress", progress);
    }

    setFill(bullet, progress) {
      if (!bullet?.classList.contains("is-active")) return;

      this.writeFill(bullet, progress);
    }

    setupIntersectionObserver() {
      if (!this.autoplay) return;

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const visible =
            !!entry && entry.isIntersecting && entry.intersectionRatio >= 0.2;

          if (visible === this.isInViewport) return;
          this.isInViewport = visible;

          if (this.isInViewport) {
            this.resumeSlideMedia(this.visuals[this.current]);
            this.startProgress({ resume: true });
            return;
          }

          this.stopProgress(true, true);
          this.pauseSlideMedia(this.visuals[this.current], { reset: false });
        },
        { threshold: [0, 0.2, 1] }
      );

      this.intersectionObserver.observe(this.el);
    }

    getCurrentSlideDelay() {
      if (!this.matchVideoDuration) return this.autoplayDelay;

      const currentVisual = this.visuals[this.current];
      if (!currentVisual) return this.autoplayDelay;

      let durationSeconds = parseFloat(
        currentVisual.dataset.videoDuration || "0"
      );
      if (!Number.isFinite(durationSeconds)) durationSeconds = 0;

      const htmlVideo = currentVisual.querySelector(HTML_VIDEO_SELECTOR);
      if (
        htmlVideo &&
        Number.isFinite(htmlVideo.duration) &&
        htmlVideo.duration > 0
      ) {
        durationSeconds = Math.max(durationSeconds, htmlVideo.duration);
      }

      // The video setting reports milliseconds, a loaded <video> reports seconds.
      if (durationSeconds > 1000) durationSeconds = durationSeconds / 1000;

      if (durationSeconds <= 0) return this.autoplayDelay;

      let remainingSeconds = durationSeconds;
      if (
        htmlVideo &&
        Number.isFinite(htmlVideo.currentTime) &&
        htmlVideo.currentTime >= 0 &&
        htmlVideo.currentTime < durationSeconds
      ) {
        remainingSeconds = Math.max(durationSeconds - htmlVideo.currentTime, 0);
      }

      return Math.max(Math.ceil(remainingSeconds * 1000 - SLIDE_FADE_MS), 0);
    }
  }

  const destroyMediaContentSlider = (section) => {
    if (!section || !section.classList.contains("media-content-slider-section"))
      return;

    const instance = sectionInstances.get(section);
    if (!instance) return;

    instance.destroy();
    sectionInstances.delete(section);
  };

  const initMediaContentSlider = (section) => {
    if (!section || !section.classList.contains("media-content-slider-section"))
      return;

    const root = section.querySelector(".media-content-slider");
    if (!root) return;

    destroyMediaContentSlider(section);
    sectionInstances.set(section, new MediaContentSlider(root));

    // Placeholder SVGs share <defs> ids, so duplicates break placeholders in other sections.
    root.querySelectorAll(".placeholder-svg defs").forEach((defs) => {
      defs.remove();
    });
  };

  initMediaContentSlider(document.currentScript.parentElement);

  if (!window.__themeMediaContentSliderEvents) {
    window.__themeMediaContentSliderEvents = true;

    document.addEventListener("shopify:section:load", function (event) {
      initMediaContentSlider(event.target);
    });

    document.addEventListener("shopify:section:unload", function (event) {
      destroyMediaContentSlider(event.target);
    });
  }
})();
